import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    type TextFieldProps,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { CalendarEvent, CalendarCategory } from '../../hooks/useCalendar';
import { formatShort, rangeOf, type IsoDate } from '../../lib/dates';
import { apiFetch } from '../../lib/api';

/**
 * Create or edit a job (or a block of leave).
 *
 * The legacy "Create Event" form asked for the job, the date range and the staff
 * all at once, and rejected the combination of a job number and a leave category
 * with an error message after submit. Here the two are a mode switch, so the
 * invalid state cannot be expressed — which is the same rule the database's
 * check constraint enforces.
 *
 * Crew is deliberately NOT asked for here. A job is created once and crewed many
 * times; bundling them meant re-opening the whole form to add one more person.
 */

export interface EventDraft {
    readonly jobNumber: string | null;
    readonly clientName: string | null;
    readonly siteAddress: string | null;
    readonly categoryId: number | null;
    readonly colour: string | null;
    readonly notes: string | null;
    readonly startsOn: IsoDate | null;
    readonly endsOn: IsoDate | null;
    /**
     * Days added to the job's window by this edit, and whether the crew already
     * on the job should be rostered onto them. Empty unless the user extended
     * the dates and chose to bring the crew.
     */
    readonly carryCrewTo: readonly IsoDate[];
    /**
     * The crew to put on those days, resolved while the dialog was open.
     *
     * Carried in the draft rather than re-queried after the save: the dialog
     * already fetched exactly this list to show the clash flags, and re-asking
     * afterwards would be a second round trip for data in hand — and would have
     * to trust the save response's id to do it.
     */
    readonly carryCrewIds: readonly number[];
}

interface CrewMember {
    readonly staffId: number;
    readonly name: string;
    readonly colour: string | null;
    readonly titleName: string | null;
    readonly alreadyOn: readonly IsoDate[];
    readonly clashes: readonly { readonly date: IsoDate; readonly label: string }[];
}

interface ExtensionPreview {
    readonly dates: readonly IsoDate[];
    readonly crew: readonly CrewMember[];
    readonly orphaned: number;
}

interface EventDialogProps {
    readonly open: boolean;
    /** null = creating. */
    readonly event: CalendarEvent | null;
    readonly categories: readonly CalendarCategory[];
    readonly defaultRange: { from: IsoDate; to: IsoDate };
    readonly saving: boolean;
    readonly error: string | null;
    readonly onClose: () => void;
    readonly onSave: (draft: EventDraft) => void;
    readonly onArchive: (event: CalendarEvent, archived: boolean) => void;
    readonly onDelete: (event: CalendarEvent) => void;
}

/** The colours the legacy calendar actually used, so ported jobs keep their look. */
const PALETTE = [
    '#098BFF', '#3AFF0A', '#FF7702', '#F7FF02', '#FF779C',
    '#3C0AFF', '#FF26A5', '#FF0707', '#860FFF', '#04F7FF',
] as const;

/** A row of the job register, as the job picker shows it. */
interface RegisterJob {
    readonly jobNumber: number;
    readonly address: string | null;
    readonly suburb: string | null;
    readonly clientName: string | null;
}

/** The row title the calendar shows, from the register's address and suburb. */
function registerAddress(job: RegisterJob): string {
    return [job.address, job.suburb]
        .map((v) => v?.trim() ?? '')
        .filter((v) => v !== '')
        .join(' ');
}

function blank(v: string | null): string {
    return v === null ? '' : v;
}

export function EventDialog({
    open,
    event,
    categories,
    defaultRange,
    saving,
    error,
    onClose,
    onSave,
    onArchive,
    onDelete,
}: EventDialogProps): React.JSX.Element {
    const [mode, setMode] = useState<'job' | 'leave'>('job');
    const [jobNumber, setJobNumber] = useState('');
    const [clientName, setClientName] = useState('');
    const [siteAddress, setSiteAddress] = useState('');
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const [colour, setColour] = useState<string>(PALETTE[0]);
    const [notes, setNotes] = useState('');
    const [startsOn, setStartsOn] = useState<string>('');
    const [endsOn, setEndsOn] = useState<string>('');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [carryCrew, setCarryCrew] = useState(false);
    const [preview, setPreview] = useState<ExtensionPreview | null>(null);
    const [previewing, setPreviewing] = useState(false);
    const [registerJobs, setRegisterJobs] = useState<readonly RegisterJob[]>([]);
    const [jobsLoading, setJobsLoading] = useState(false);
    const [jobsError, setJobsError] = useState<string | null>(null);

    // Jobs are registered first and then scheduled, so the picker offers the
    // register's active jobs. Fetched on each open: a job added a minute ago on
    // the Jobs screen should be pickable without a page reload.
    useEffect(() => {
        if (!open) {
            return;
        }
        let cancelled = false;
        setJobsLoading(true);
        setJobsError(null);
        apiFetch<{ jobs: readonly RegisterJob[] }>('/calendar/jobs')
            .then((r) => {
                if (!cancelled) {
                    setRegisterJobs(r.jobs);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setJobsError(err instanceof Error ? err.message : 'Could not load jobs');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setJobsLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [open]);

    /**
     * The picker's options. A job being edited may carry a number that is not
     * active any more (finished, or typed in by hand on the legacy screen); it
     * is kept as an option so opening and saving the job does not blank it.
     */
    const jobOptions = useMemo<readonly RegisterJob[]>(() => {
        const current = jobNumber.trim();
        if (current === '' || registerJobs.some((j) => String(j.jobNumber) === current)) {
            return registerJobs;
        }
        const asNumber = Number(current);
        if (!Number.isInteger(asNumber)) {
            return registerJobs;
        }
        return [{ jobNumber: asNumber, address: null, suburb: null, clientName: null }, ...registerJobs];
    }, [registerJobs, jobNumber]);

    const selectedJob = useMemo(
        () => jobOptions.find((j) => String(j.jobNumber) === jobNumber.trim()) ?? null,
        [jobOptions, jobNumber],
    );

    /**
     * The picked job is on the register, so its address comes from there and is
     * not typed here — edit it on the Jobs screen. A kept legacy number has no
     * register row to take it from, so that one stays editable.
     */
    const addressFromRegister =
        selectedJob !== null && registerJobs.some((j) => j.jobNumber === selectedJob.jobNumber);

    // Reload the form whenever it is opened for a different job, so an edit
    // never shows the previous job's values for a moment.
    useEffect(() => {
        if (!open) {
            return;
        }
        setConfirmDelete(false);
        setCarryCrew(false);
        setPreview(null);
        if (event === null) {
            setMode('job');
            setJobNumber('');
            setClientName('');
            setSiteAddress('');
            setCategoryId(null);
            setColour(PALETTE[0]);
            setNotes('');
            setStartsOn(defaultRange.from);
            setEndsOn(defaultRange.to);
            return;
        }
        setMode(event.categoryId === null ? 'job' : 'leave');
        setJobNumber(blank(event.jobNumber));
        setClientName(blank(event.clientName));
        setSiteAddress(blank(event.siteAddress).trim());
        setCategoryId(event.categoryId);
        setColour(event.colour ?? PALETTE[0]);
        setNotes(blank(event.notes));
        setStartsOn(blank(event.startsOn));
        setEndsOn(blank(event.endsOn));
    }, [open, event, defaultRange]);

    /**
     * The days this edit adds to the job's window.
     *
     * Both ends count: work is usually extended at the end, but a job that
     * starts earlier than planned is the same operation in reverse.
     */
    const addedDates = useMemo<IsoDate[]>(() => {
        if (event === null || startsOn === '' || endsOn === '' || endsOn < startsOn) {
            return [];
        }
        const oldStart = event.startsOn;
        const oldEnd = event.endsOn;
        if (oldStart === null || oldEnd === null) {
            return [];
        }
        const out: IsoDate[] = [];
        if (startsOn < oldStart) {
            for (const d of rangeOf(startsOn, oldStart)) {
                if (d < oldStart) {
                    out.push(d);
                }
            }
        }
        if (endsOn > oldEnd) {
            for (const d of rangeOf(oldEnd, endsOn)) {
                if (d > oldEnd) {
                    out.push(d);
                }
            }
        }
        return out;
    }, [event, startsOn, endsOn]);

    const removedDays = useMemo<number>(() => {
        if (event === null || event.startsOn === null || event.endsOn === null) {
            return 0;
        }
        if (startsOn === '' || endsOn === '') {
            return 0;
        }
        let n = 0;
        for (const d of rangeOf(event.startsOn, event.endsOn)) {
            if (d < startsOn || d > endsOn) {
                n += 1;
            }
        }
        return n;
    }, [event, startsOn, endsOn]);

    // Ask the server who is on the job and what they clash with. The new days
    // are usually outside the range the grid has loaded, so this cannot be
    // answered from what the page already holds.
    useEffect(() => {
        if (event === null || addedDates.length === 0) {
            setPreview(null);
            return;
        }
        let cancelled = false;
        setPreviewing(true);
        apiFetch<ExtensionPreview>(`/calendar/events/${String(event.id)}/extension-preview`, {
            method: 'POST',
            body: JSON.stringify({ dates: addedDates, startsOn, endsOn }),
        })
            .then((r) => {
                if (!cancelled) {
                    setPreview(r);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPreview(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setPreviewing(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [event, addedDates, startsOn, endsOn]);

    const datesValid = startsOn === '' || endsOn === '' || endsOn >= startsOn;
    const canSave =
        !saving && datesValid && (mode === 'job' ? jobNumber.trim() !== '' : categoryId !== null);

    function save(): void {
        if (!canSave) {
            return;
        }
        const trimmed = (v: string): string | null => (v.trim() === '' ? null : v.trim());
        onSave({
            jobNumber: mode === 'job' ? trimmed(jobNumber) : null,
            clientName: mode === 'job' ? trimmed(clientName) : null,
            siteAddress: mode === 'job' ? trimmed(siteAddress) : null,
            categoryId: mode === 'leave' ? categoryId : null,
            colour,
            notes: trimmed(notes),
            startsOn: startsOn === '' ? null : startsOn,
            endsOn: endsOn === '' ? null : endsOn,
            carryCrewTo: carryCrew ? addedDates : [],
            carryCrewIds: carryCrew ? (preview?.crew.map((m) => m.staffId) ?? []) : [],
        });
    }

    const archived = event !== null && event.archivedAt !== null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 800 }}>{event === null ? 'New job' : 'Edit job'}</DialogTitle>

            <DialogContent dividers>
                {/* A job and a leave category are mutually exclusive in the
                    database, so they are a mode here rather than two fields that
                    can both be filled and then rejected. */}
                <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    value={mode}
                    onChange={(_e, v: 'job' | 'leave' | null) => {
                        if (v !== null) {
                            setMode(v);
                        }
                    }}
                    sx={{ mb: 2 }}
                >
                    <ToggleButton value="job">Job</ToggleButton>
                    <ToggleButton value="leave">Leave / other</ToggleButton>
                </ToggleButtonGroup>

                <Stack gap={1.5}>
                    {mode === 'job' ? (
                        <>
                            <Autocomplete
                                size="small"
                                options={jobOptions}
                                value={selectedJob}
                                loading={jobsLoading}
                                onChange={(_e, job) => {
                                    if (job === null) {
                                        setJobNumber('');
                                        setSiteAddress('');
                                        return;
                                    }
                                    setJobNumber(String(job.jobNumber));
                                    // Only a register row carries these; the kept
                                    // legacy option must not blank what is there.
                                    if (registerJobs.some((j) => j.jobNumber === job.jobNumber)) {
                                        setSiteAddress(registerAddress(job));
                                    }
                                    if (job.clientName !== null) {
                                        setClientName(job.clientName);
                                    }
                                }}
                                // The box shows just the number; the address has
                                // its own field below. Typing still matches on the
                                // address and suburb — see filterOptions.
                                getOptionLabel={(job) => `Job ${String(job.jobNumber)}`}
                                filterOptions={(options, { inputValue }) => {
                                    const q = inputValue.trim().toLowerCase().replace(/^job\s*/, '');
                                    if (q === '') {
                                        return [...options];
                                    }
                                    return options.filter((job) =>
                                        `${String(job.jobNumber)} ${registerAddress(job)} ${job.clientName ?? ''}`
                                            .toLowerCase()
                                            .includes(q),
                                    );
                                }}
                                isOptionEqualToValue={(a, b) => a.jobNumber === b.jobNumber}
                                renderOption={(props, job) => {
                                    // As in SuburbField: MUI types the key as any, and
                                    // React 19 warns if it is spread. The job number is
                                    // a stable key of our own.
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unused-vars
                                    const { key: _muiKey, ...rest } = props;
                                    const address = registerAddress(job);
                                    return (
                                        <Box component="li" key={job.jobNumber} {...rest}>
                                            <Box>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                                                    {`Job ${String(job.jobNumber)}`}
                                                    {address === '' ? '' : ` · ${address}`}
                                                </Typography>
                                                {job.clientName === null ? null : (
                                                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                                                        {job.clientName}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                }}
                                noOptionsText={jobsError ?? 'No active job matches'}
                                renderInput={(params) => (
                                    <TextField
                                        {...(params as TextFieldProps)}
                                        size="small"
                                        label="Job"
                                        required
                                        autoFocus
                                        error={jobsError !== null}
                                        helperText={jobsError ?? 'Active jobs from the job register'}
                                    />
                                )}
                            />
                            <TextField
                                label="Site address"
                                size="small"
                                value={siteAddress}
                                onChange={(e) => {
                                    setSiteAddress(e.target.value);
                                }}
                                helperText={
                                    addressFromRegister
                                        ? 'From the job register — change it on the Jobs screen'
                                        : 'Shown as the row title on the calendar'
                                }
                                slotProps={{ input: { readOnly: addressFromRegister } }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        bgcolor: addressFromRegister ? 'action.hover' : 'transparent',
                                    },
                                }}
                            />
                            <TextField
                                label="Client"
                                size="small"
                                value={clientName}
                                onChange={(e) => {
                                    setClientName(e.target.value);
                                }}
                            />
                        </>
                    ) : (
                        <TextField
                            select
                            label="Category"
                            size="small"
                            required
                            autoFocus
                            SelectProps={{ native: true }}
                            value={categoryId === null ? '' : String(categoryId)}
                            onChange={(e) => {
                                setCategoryId(e.target.value === '' ? null : Number(e.target.value));
                            }}
                        >
                            <option value="" />
                            {categories.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </TextField>
                    )}

                    <Stack direction="row" gap={1.5}>
                        <TextField
                            label="From"
                            type="date"
                            size="small"
                            fullWidth
                            value={startsOn}
                            onChange={(e) => {
                                setStartsOn(e.target.value);
                            }}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            label="To"
                            type="date"
                            size="small"
                            fullWidth
                            value={endsOn}
                            onChange={(e) => {
                                setEndsOn(e.target.value);
                            }}
                            error={!datesValid}
                            helperText={datesValid ? ' ' : 'Must be on or after From'}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                    </Stack>

                    {/* ─── Extending the job ──────────────────────────────
                        Changing the dates is the common edit — work runs long —
                        and the question that always follows is whether the same
                        crew come with it. Asking here, with the clashes already
                        checked, saves re-crewing the new days by hand and stops
                        someone being quietly double-booked. */}
                    {addedDates.length > 0 ? (
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'secondary.main',
                                borderRadius: 1.5,
                                p: 1.5,
                                bgcolor: 'action.hover',
                            }}
                        >
                            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.25 }}>
                                Extending by {addedDates.length}{' '}
                                {addedDates.length === 1 ? 'day' : 'days'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 1 }}>
                                {addedDates.length === 1
                                    ? formatShort(addedDates[0] ?? '')
                                    : `${formatShort(addedDates[0] ?? '')} – ${formatShort(addedDates[addedDates.length - 1] ?? '')}`}
                            </Typography>

                            <RadioGroup
                                value={carryCrew ? 'crew' : 'dates'}
                                onChange={(e) => {
                                    setCarryCrew(e.target.value === 'crew');
                                }}
                            >
                                <FormControlLabel
                                    value="dates"
                                    control={<Radio size="small" />}
                                    label={
                                        <Typography sx={{ fontSize: '0.82rem' }}>
                                            Just extend the dates
                                        </Typography>
                                    }
                                />
                                <FormControlLabel
                                    value="crew"
                                    disabled={previewing || (preview?.crew.length ?? 0) === 0}
                                    control={<Radio size="small" />}
                                    label={
                                        <Typography sx={{ fontSize: '0.82rem' }}>
                                            {previewing
                                                ? 'Checking the crew…'
                                                : (preview?.crew.length ?? 0) === 0
                                                  ? 'No crew on this job yet'
                                                  : `Also roster the current crew (${String(preview?.crew.length ?? 0)} × ${String(addedDates.length)} = ${String((preview?.crew.length ?? 0) * addedDates.length)})`}
                                        </Typography>
                                    }
                                />
                            </RadioGroup>

                            {previewing ? <CircularProgress size={16} sx={{ ml: 1 }} /> : null}

                            {/* The crew, each flagged with whatever they are already
                                committed to on the new days. Shown whichever option is
                                selected, because it is the reason to pick one. */}
                            {preview !== null && preview.crew.length > 0 ? (
                                <Stack gap={0.5} sx={{ mt: 1, maxHeight: 168, overflow: 'auto' }}>
                                    {preview.crew.map((m) => {
                                        const clashDays = new Set(m.clashes.map((c) => c.date));
                                        const labels = [...new Set(m.clashes.map((c) => c.label))];
                                        return (
                                            <Stack
                                                key={m.staffId}
                                                direction="row"
                                                alignItems="center"
                                                gap={0.75}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 9,
                                                        height: 9,
                                                        borderRadius: '50%',
                                                        bgcolor: m.colour ?? 'text.disabled',
                                                        flex: '0 0 auto',
                                                    }}
                                                />
                                                <Typography
                                                    noWrap
                                                    sx={{ fontSize: '0.78rem', flex: 1, minWidth: 0 }}
                                                >
                                                    {m.name}
                                                </Typography>
                                                {clashDays.size > 0 ? (
                                                    <Tooltip
                                                        title={`Already on ${labels.join(', ')} on ${[...clashDays].map(formatShort).join(', ')}`}
                                                    >
                                                        <Chip
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                            icon={<WarningAmberIcon sx={{ fontSize: 13 }} />}
                                                            label={`on another job ${String(clashDays.size)}d`}
                                                            sx={{ height: 19, fontSize: '0.64rem' }}
                                                        />
                                                    </Tooltip>
                                                ) : m.alreadyOn.length > 0 ? (
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={`already on ${String(m.alreadyOn.length)}d`}
                                                        sx={{ height: 19, fontSize: '0.64rem' }}
                                                    />
                                                ) : (
                                                    <Tooltip title="Free on all of these days">
                                                        <CheckCircleOutlineIcon
                                                            sx={{ fontSize: 15, color: 'success.main' }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        );
                                    })}
                                </Stack>
                            ) : null}

                            {carryCrew && preview !== null && preview.crew.some((m) => m.clashes.length > 0) ? (
                                <Alert severity="warning" sx={{ mt: 1, py: 0.25 }}>
                                    {preview.crew.filter((m) => m.clashes.length > 0).length} of the crew are on
                                    another job on some of these days. They will be added anyway — overlaps are
                                    allowed, and the calendar will flag them.
                                </Alert>
                            ) : null}
                        </Box>
                    ) : null}

                    {/* Shrinking strands crew outside the window. They are not
                        deleted, but the board stops drawing them, which looks
                        identical to having lost them. */}
                    {removedDays > 0 ? (
                        <Alert severity="warning" sx={{ py: 0.25 }}>
                            Shortening by {removedDays} {removedDays === 1 ? 'day' : 'days'}.
                            {preview !== null && preview.orphaned > 0
                                ? ` ${String(preview.orphaned)} crew assignments fall outside the new dates — they are kept, but will not show on the board.`
                                : ' Any crew on the dropped days are kept but will not show on the board.'}
                        </Alert>
                    ) : null}

                    <Box>
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.75 }}>
                            Colour
                        </Typography>
                        <Stack direction="row" gap={0.75} flexWrap="wrap">
                            {PALETTE.map((c) => (
                                <Box
                                    key={c}
                                    onClick={() => {
                                        setColour(c);
                                    }}
                                    sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: '50%',
                                        bgcolor: c,
                                        cursor: 'pointer',
                                        border: '2px solid',
                                        borderColor: colour === c ? 'common.white' : 'transparent',
                                        '&:hover': { transform: 'scale(1.12)' },
                                        transition: 'transform 100ms',
                                    }}
                                />
                            ))}
                        </Stack>
                        <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', mt: 0.75 }}>
                            Marks the job&apos;s row. Staff chips take their colour from the person&apos;s skillset.
                        </Typography>
                    </Box>

                    <TextField
                        label="Notes"
                        size="small"
                        multiline
                        minRows={2}
                        value={notes}
                        onChange={(e) => {
                            setNotes(e.target.value);
                        }}
                    />
                </Stack>

                {error !== null ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                ) : null}

                {event !== null ? (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Stack direction="row" gap={1} alignItems="center">
                            <Tooltip
                                title={
                                    archived
                                        ? 'Put this job back on the board'
                                        : 'Keep the job and its crew history, but take it off the board'
                                }
                            >
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                                    onClick={() => {
                                        onArchive(event, !archived);
                                    }}
                                    disabled={saving}
                                >
                                    {archived ? 'Restore' : 'Archive'}
                                </Button>
                            </Tooltip>
                            <Box sx={{ flex: 1 }} />
                            {confirmDelete ? (
                                <>
                                    <Typography sx={{ fontSize: '0.75rem', color: 'error.main' }}>
                                        Delete job and all its crew?
                                    </Typography>
                                    <Button
                                        size="small"
                                        color="error"
                                        variant="contained"
                                        onClick={() => {
                                            onDelete(event);
                                        }}
                                        disabled={saving}
                                    >
                                        Delete
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            setConfirmDelete(false);
                                        }}
                                    >
                                        Keep
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteOutlineIcon />}
                                    onClick={() => {
                                        setConfirmDelete(true);
                                    }}
                                    disabled={saving}
                                >
                                    Delete
                                </Button>
                            )}
                        </Stack>
                    </>
                ) : null}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={save} disabled={!canSave}>
                    {event === null ? 'Create' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
